import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { Users, Image, HardDrive, TrendingUp, Crown, Shield, AlertTriangle, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";


const TIER_LABELS: Record<string, string> = {
  free: "Free",
  basic: "Loop Family (Annual)",
  mid: "Loop Family (Monthly)",
  premium: "Loop Recruit",
};

const TIER_COLORS: Record<string, string> = {
  free: "text-slate-400 bg-slate-400/10 border-slate-400/20",
  basic: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  mid: "text-purple-400 bg-purple-400/10 border-purple-400/20",
  premium: "text-[#E0A458] bg-[#E0A458]/10 border-[#E0A458]/20",
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const gb = bytes / (1024 ** 3);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / (1024 ** 2);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

function formatDate(iso: string): string {
  if (!iso) return "--";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function StatCard({ label, value, icon: Icon, sub }: { label: string; value: string | number; icon: any; sub?: string }) {
  return (
    <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-5">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="h-5 w-5 text-[#E0A458]" />
        <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white font-montserrat">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const isAdmin = !!(user as any)?.isAdmin;

  const { data: stats, isLoading: statsLoading, isError: statsError, error: statsErrorObj } = useQuery<any>({
    queryKey: ["/api/admin/stats"],
    enabled: isAdmin,
  });

  const { data: users, isLoading: usersLoading, isError: usersError, error: usersErrorObj } = useQuery<any[]>({
    queryKey: ["/api/admin/users"],
    enabled: isAdmin,
  });

  const hasError = statsError || usersError || stats === null || users === null;

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#E0A458]" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 text-[#E0A458]/40 mx-auto mb-4" />
          <p className="text-white font-montserrat font-bold text-xl">Access Denied</p>
          <p className="text-slate-400 text-sm mt-1">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const paid = (stats?.basic_count ?? 0) + (stats?.mid_count ?? 0) + (stats?.premium_count ?? 0);
  // Annual plans normalized to monthly: $99/12 and $199/12; monthly plan at $12.99
  const mrr = Math.round(
    (stats?.basic_count ?? 0) * (99 / 12) +
    (stats?.mid_count ?? 0) * 12.99 +
    (stats?.premium_count ?? 0) * (199 / 12)
  );

  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-5 shadow-lg shadow-black/25 mb-8 flex items-center gap-3">
          <Crown className="h-6 w-6 text-[#E0A458]" />
          <div>
            <h1 className="font-montserrat font-bold text-2xl text-white">Admin Dashboard</h1>
            <p className="text-slate-400 text-sm">LegacyLoop platform overview</p>
          </div>
        </div>

        {/* Error / debug banner */}
        {hasError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-red-300 font-medium text-sm">
                {(stats === null || users === null)
                  ? "Access denied — the API returned 403. Check that the backend has deployed the admin routes (Render may still be building)."
                  : "Failed to load admin data — check Render logs for errors on GET /api/admin/stats or /api/admin/users."}
              </p>
              {(statsErrorObj || usersErrorObj) && (
                <p className="text-red-400/70 text-xs mt-1 font-mono break-all">
                  {String((statsErrorObj || usersErrorObj)?.message || "")}
                </p>
              )}
            </div>
            <button
              onClick={handleRefresh}
              className="shrink-0 flex items-center gap-1.5 text-xs text-[#E0A458] hover:text-[#f0b467] transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        )}

        {/* Summary cards */}
        {statsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-5 h-28 animate-pulse" />
            ))}
          </div>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard label="Total Users" value={stats.total_users ?? 0} icon={Users} />
              <StatCard label="Paid Subscribers" value={paid} icon={Crown} sub={`${stats.active_subscribers ?? 0} active`} />
              <StatCard label="Total Media" value={stats.total_media ?? 0} icon={Image} sub={`${stats.video_count ?? 0} videos · ${stats.image_count ?? 0} photos`} />
              <StatCard label="Storage Used" value={formatBytes(Number(stats.total_storage_bytes ?? 0))} icon={HardDrive} />
            </div>

            {/* MRR + tier breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard label="Est. MRR" value={`$${mrr.toLocaleString()}`} icon={TrendingUp} sub="based on plan counts" />
              <StatCard label="Loop Family (Annual)" value={stats.basic_count ?? 0} icon={Users} sub="$99/yr" />
              <StatCard label="Loop Family (Monthly)" value={stats.mid_count ?? 0} icon={Users} sub="$12.99/mo" />
              <StatCard label="Loop Recruit" value={stats.premium_count ?? 0} icon={Users} sub="$199/yr" />
            </div>
          </>
        ) : null}

        {/* Users table */}
        <div>
          <h2 className="font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3 mb-4">
            All Users
            {users && <span className="text-slate-400 font-normal text-sm ml-2">({users.length})</span>}
          </h2>

          <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 overflow-hidden">
            {usersLoading ? (
              <div className="p-6 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-10 bg-[#1B263B]/60 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#1B263B] text-left">
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">User</th>
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">Plan</th>
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">Storage Used</th>
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">Media</th>
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">Journals</th>
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">Athletes</th>
                      <th className="px-4 py-3 text-xs text-slate-400 uppercase tracking-wide font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users?.map((u: any) => {
                      const tier = (u.subscription_tier || "free").toLowerCase();
                      const tierLabel = TIER_LABELS[tier] ?? tier;
                      const tierColor = TIER_COLORS[tier] ?? TIER_COLORS.free;
                      return (
                        <tr key={u.id} className="border-b border-[#1B263B]/50 last:border-b-0 hover:bg-[#1B263B]/30 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-white font-medium">
                                {u.athlete_names
                                  ? u.athlete_names.trim()
                                  : <span className="text-slate-500 italic">No athlete</span>}
                              </p>
                              <p className="text-slate-400 text-xs">{u.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${tierColor}`}>
                              {tierLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300">{formatBytes(Number(u.storage_used ?? 0))}</td>
                          <td className="px-4 py-3 text-slate-300">{u.media_count ?? 0}</td>
                          <td className="px-4 py-3 text-slate-300">{u.journal_count ?? 0}</td>
                          <td className="px-4 py-3 text-slate-300">{u.athlete_count ?? 0}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{formatDate(u.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {users?.length === 0 && (
                  <div className="p-8 text-center text-slate-400 text-sm">No users yet.</div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
